import './style.scss'

import { appSelectors } from '@appStore/reducers/app.ts'
import { connectorAPI } from 'actions/connector.ts'
import calendar from 'assets/image/icons/svg/calendar.svg'
import 'react-date-range/dist/styles.css' // main css file
import 'react-date-range/dist/theme/default.css' // theme css file
import clsx from 'clsx'
import {
	workspaceApi,
	WorkSpaceUI,
	workspaceUtils
} from 'features/workspace/index.ts'
import { useEffect, useId, useRef, useState } from 'react'
import { DateRangePicker, type Range } from 'react-date-range'
import { useAutomationEnded } from 'shared/connector/hooks.ts'
import { useAppSelector } from 'shared/hooks/store.ts'
import { useLatest } from 'shared/hooks/useLatest.ts'
import { typo } from 'shared/styles'
import { Button, LoadingButton } from 'shared/ui'

import type { LocalRange } from './adapter.ts'

import LinkedInBlock from '../../atoms/LinkedInBlock'
import { DailyLimitsEditor } from '../dailyLimitsEditor'
import css from './AccountProfile.module.scss'
import * as adapter from './adapter.ts'

export function AccountProfile() {
	const popoverId = useId()
	const popoverRef = useRef<HTMLFormElement | null>(null)

	const end = useAppSelector(workspaceUtils.selectNowWithOffset)
	const email = useAppSelector(
		state => appSelectors.persona(state)?.email ?? ''
	)

	const [rangeState, setRangeState] = useState<LocalRange>(() => ({
		end: end.endOf('day'),
		start: end.startOf('month')
	}))

	const [pickerState, setPickerState] = useState<Range[]>(() =>
		adapter.fromDay(rangeState)
	)

	const fullName = useAppSelector(appSelectors.fullName)

	const {
		data: statistics,
		isFetching,
		isSuccess
	} = workspaceApi.useStatisticsQuery({
		start: rangeState.start.toISOString(),

		// eslint-disable-next-line perfectionist/sort-objects
		end: rangeState.end.toISOString()
	})

	const setDate = () => {
		if (isFetching) {
			return
		}
		const adapted = adapter.fromPicker(pickerState)
		setRangeState(adapted)
	}

	useEffect(() => {
		if (!isFetching && isSuccess) {
			popoverRef.current?.hidePopover()
		}
	}, [isFetching, isSuccess])

	useAutomationEnded(() => {
		workspaceUtils.refetchStatistics()
		connectorAPI.getSnailyProfile()
	})

	const datedRange = [rangeState.start, rangeState.end]
		.map(date => date.format('DD MMM YYYY'))
		.join(' - ')

	const cancel = () => popoverRef.current?.hidePopover()

	const rangeRef = useLatest(rangeState)

	useEffect(() => {
		const restoreOriginal = (event: Event) => {
			const isClose = 'newState' in event && event.newState === 'closed'
			if (isClose) {
				const oldValue = adapter.fromDay(rangeRef.current)

				setPickerState(oldValue)
			}
		}

		popoverRef.current?.addEventListener('toggle', restoreOriginal)

		return () =>
			popoverRef.current?.removeEventListener('toggle', restoreOriginal)
	}, [rangeRef])

	return (
		<article className={css.root}>
			<h2 className={clsx(css.name, typo.largeButton)}>{fullName}</h2>
			{email && <p className={clsx(css.email, typo.buttonSm)}>{email}</p>}

			<hr className={css.line} />
			<LinkedInBlock />
			<DailyLimitsEditor customStatistics={statistics ?? {}} />
			<hr className={css.line} />

			<h3 className={clsx(css.date, typo.buttonSmB)}>
				<span>Performed </span>

				<span>{datedRange}</span>

				<button
					aria-label='Open calendar'
					className={css.calendar}
					// @ts-ignore Recheck in React 19
					popovertarget={popoverId}
					popovertargetaction='toggle'
					title='Open calendar'
					type='button'
				>
					<img alt='calendar' src={calendar} width={14} />
				</button>
			</h3>

			<form
				className={css.modal}
				id={popoverId}
				onReset={cancel}
				onSubmit={e => {
					e.preventDefault()
					setDate()
				}}
				// @ts-ignore Recheck in React 19
				popover='auto'
				ref={popoverRef}
			>
				<DateRangePicker
					direction='horizontal'
					maxDate={end.toDate()}
					months={2}
					moveRangeOnFirstSelection={false}
					onChange={({ selection }) => setPickerState([selection])}
					ranges={pickerState}
					// showSelectionPreview={true}
					weekStartsOn={1}
				/>
				<div className={css.buttons}>
					<Button className={css.button} type='reset'>
						Cancel
					</Button>

					<LoadingButton
						className={css.button}
						isLoading={isFetching}
						type='submit'
					>
						Apply
					</LoadingButton>
				</div>
			</form>

			<WorkSpaceUI.Performed statistics={statistics} />
		</article>
	)
}
